import type { IAddress } from '@/types/address';
import type { ObjectID, Timestamp } from '@/types/common';

export enum DocumentStatus {
	TO_VALIDATE = 'TO_VALIDATE',
	VALID = 'VALID',
	REFUSED = 'REFUSED',
	TO_VERIFY = 'TO_VERIFY',
	PENDING_VERIFICATION = 'PENDING_VERIFICATION',
	VERIFIED = 'VERIFIED',
	REJECTED = 'REJECTED',
	REPRISE = 'REPRISE',
	EXPIRED = 'EXPIRED',
}

export enum EWorkerDocumentType {
	BTP_CARD = 'btp-card',
	HABILITATION = 'habilitation',
	DEGREE = 'degree',
	MEDICAL_CHECK_UP = 'medical-check-up',
	OFFICIAL_DOCUMENTS = 'official-documents',
}

export type IHabilitationDocument = {
	label: string;
	value: string;
	type: string;
	duration: number;
};

export interface WorkerDocumentOption {
	label: string;
	value: string;
	type: string;
	category: string;
	duration: number | null;
	fields: string | null;
	acceptedFormats: string | null;
	maxSizeMb: number | null;
	ocrEnabled: boolean;
	ocrProvider: string | null;
	ocrDocumentType: string | null;
	ocrFieldMapping: unknown;
	idCheckhub: string | null;
	evoliaCode: string | null;
}

export interface WorkerDocument {
	acquisitionDate: number;
	documentName: string;
	expirationDate: number;
	habilitationCode: string;
	idDocument: string;
	organisationCode: string;
	organisationName: string;
	type: string;
}

export interface InterimWorkerDocuments {
	[EWorkerDocumentType.BTP_CARD]: WorkerDocument[];
	[EWorkerDocumentType.HABILITATION]: WorkerDocument[];
	[EWorkerDocumentType.DEGREE]: WorkerDocument[];
	[EWorkerDocumentType.MEDICAL_CHECK_UP]: WorkerDocument[];
	[EWorkerDocumentType.OFFICIAL_DOCUMENTS]: WorkerDocument[];
}

export interface IWorkerDocument {
	idDocument: ObjectID;
	documentName: string;
	acquisitionDate?: Timestamp;
	expirationDate?: Timestamp;
	note?: string;
	type: EWorkerDocumentType;
	isValid?: boolean;
	status?: DocumentStatus;
	filePath?: string;
	fileUrl?: string;
	fileSize?: number;
	mimeType?: string;
	issueDate?: Timestamp;
	socialSecurityNumber?: string;
	supplier?: string;
	documentAddress?: string;
	employer?: string;
	workPeriodStart?: Timestamp;
	workPeriodEnd?: Timestamp;
	positions?: string;
	adeliNumber?: string;
	cpsCardNumber?: string;
	qualification?: string;
	issuedBy?: string;
	pieceNumber?: string;
	specialization?: string;
	monthsCount?: number;
	workStation?: string;
	examType?: string;
	visitDate?: Timestamp;
	comments?: string;
	aptitude?: string;
	policeNumber?: string;
	accountHolder?: string;
	iban?: string;
	bic?: string;
	bank?: string;
	domiciliation?: string;
	bankName?: string;
}

export interface IWorkerDocumentBtpCard extends IWorkerDocument {
	cardNumber?: string;
	cardStatus?: string;
}

export interface IWorkerDocumentHabilitation extends IWorkerDocument {
	habilitationCode?: string;
	organisationCode?: string;
	organisationName?: string;
}

export interface IWorkerDocumentDegree extends IWorkerDocument {
	degreeCode: string;
	buisnessSectorCode: string;
	buisnessSector: string;
	graduated: string;
	degreeObtainedAt?: Partial<IAddress>;
}

export interface IWorkerDocumentOfficalDocuments extends IWorkerDocument {
	officialDocumentType: string;
	documentId?: string;
	address?: Partial<IAddress>;
	prefecture?: string;
	prefectureControlDate?: Timestamp;
	prefectureReturnDate?: Timestamp;
	documentStatus?: string;
}
